import { useState, useEffect } from "react";
import {
  Grid,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  useMediaQuery,
  useTheme
} from "@mui/material";
import Layout from "../../components/Layout";
import Button from "../../components/Button";
import NoData from "../dashboard/components/NotFoundCard/NoData";
import NoAccount from "../../assets/images/emptyacct.png";
import BreadCrumb from "../../components/BreadCrumb";
import useStyles from "./styles";
import InputGroup from "../../components/FormComponent/InputGroup";
import Modal from "../../components/Modal";
import Select from "react-select";
import currencyCodes from "currency-codes";
import { Icon } from "@iconify/react/dist/iconify.js";
import { httprequest } from "../../data/api";
import { toast } from "react-toastify";

const PROFILE_ENDPOINT = "/api/v1/profile";

const MyBank = () => {
  const classes = useStyles();
  const [isAddBankModalOpen, setIsAddBankModalOpen] = useState(false);
  const [isEditBankModalOpen, setIsEditBankModalOpen] = useState(false);
  const [isDeleteBankModalOpen, setIsDeleteBankModalOpen] = useState(false);
  const [selectedBankIndex, setSelectedBankIndex] = useState(null);
  const [bankDetails, setBankDetails] = useState({
    bank_name: "",
    bank_code: "",
    account_name: "",
    account_number: "",
    currency: ""
  });
  const [bankOptions, setBankOptions] = useState([]);
  const [currencyOptions, setCurrencyOptions] = useState([]);
  const [bankAccounts, setBankAccounts] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const shouldCollapse = isMobile || bankAccounts.length > 5;
  const [expandedAccordion, setExpandedAccordion] = useState(false);

  const getBankDetails = async () => {
    try {
      const res = await httprequest.get(PROFILE_ENDPOINT);
      const rawBanks = res?.data?.data?.bank_details || [];
      const validBanks = rawBanks.filter(bank => bank && bank.bank_name && bank.account_number);
      setBankAccounts(validBanks);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("Error fetching bank details:", error);
    }
  };

  const getBankOptions = async () => {
    try {
      const res = await httprequest.get("/api/v1/points/banks");
      const banksData = Array.isArray(res?.data?.data) ? res.data.data : (Array.isArray(res?.data) ? res.data : []);
      const banks = banksData.map((bank) => ({
        value: bank.name,
        label: bank.name,
        code: bank.code
      }));
      banks.sort((a, b) => a.label.localeCompare(b.label));
      setBankOptions(banks);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("Error fetching banks:", error);
    }
  };

  useEffect(() => {
    const loadCurrencyOptions = () => {
      try {
        const currencies = currencyCodes.data.map((currency) => ({
          value: currency.code,
          label: `${currency.currency} (${currency.code})`
        }));
        currencies.sort((a, b) => a.label.localeCompare(b.label));
        setCurrencyOptions(currencies);
      } catch (error) {
        setCurrencyOptions([
          { value: "USD", label: "US Dollar (USD)" },
          { value: "EUR", label: "Euro (EUR)" },
          { value: "GBP", label: "British Pound (GBP)" },
          { value: "NGN", label: "Nigerian Naira (NGN)" }
        ]);
      }
    };
    loadCurrencyOptions();
    getBankDetails();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setBankDetails((prev) => ({ ...prev, [name]: value }));
  };

  const handleCurrencyChange = (selectedOption) => {
    setBankDetails((prev) => ({
      ...prev,
      currency: selectedOption ? selectedOption.value : ""
    }));
  };

  const getSelectedCurrency = () => {
    return currencyOptions.find((option) => option.value === bankDetails.currency) || null;
  };

  const toggleAddBankModal = () => {
    const isOpening = !isAddBankModalOpen;
    setIsAddBankModalOpen(isOpening);
    if (isOpening && bankOptions.length === 0) {
      getBankOptions();
    }
  };

  const handleSubmit = async () => {
    if (!bankDetails.bank_name || !bankDetails.account_number || !bankDetails.account_name || !bankDetails.currency) {
      toast.error("Please fill all required fields");

      return;
    }

    setIsLoading(true);

    if (bankDetails.bank_code && bankDetails.account_number) {
      try {
        const validationRes = await httprequest.get(`/api/v1/points/resolve-account?account_number=${bankDetails.account_number}&bank_code=${bankDetails.bank_code}`);
        if (validationRes.data && validationRes.data.status === false) {
          toast.error(validationRes.data.message || "Invalid account details");
          setIsLoading(false);

          return;
        }
      } catch (error) {
        toast.error(error?.response?.data?.message || "Invalid account details");
        setIsLoading(false);

        return;
      }
    }

    try {
      const payload = {
        bank_details: [{
          account_name: bankDetails.account_name,
          account_number: bankDetails.account_number,
          bank_name: bankDetails.bank_name,
          bank_code: bankDetails.bank_code,
          currency: bankDetails.currency,
          action: "add"
        }]
      };

      const response = await httprequest.patch(PROFILE_ENDPOINT, payload);
      toast.success(response?.data?.message || "Bank account added successfully");

      setBankDetails({
        bank_name: "",
        account_name: "",
        account_number: "",
        currency: "",
        bank_code: ""
      });
      toggleAddBankModal();
      getBankDetails();
    } catch (error) {
      toast.error(error?.response?.data?.message || "Failed to add bank account");
    } finally {
      setIsLoading(false);
    }
  };

  const openEditModal = (index) => {
    setSelectedBankIndex(index);
    setBankDetails({ ...bankAccounts[index] });
    setIsEditBankModalOpen(true);
    if (bankOptions.length === 0) {
      getBankOptions();
    }
  };

  const closeEditModal = () => {
    setIsEditBankModalOpen(false);
    setSelectedBankIndex(null);
    setBankDetails({ bank_name: "", bank_code: "", account_name: "", account_number: "", currency: "" });
  };

  const handleEditSubmit = async () => {
    if (!bankDetails.bank_name || !bankDetails.account_number || !bankDetails.account_name || !bankDetails.currency) {
      toast.error("Please fill all required fields");

      return;
    }
    setIsLoading(true);

    if (bankDetails.bank_code && bankDetails.account_number) {
      try {
        const validationRes = await httprequest.get(`/api/v1/points/resolve-account?account_number=${bankDetails.account_number}&bank_code=${bankDetails.bank_code}`);
        if (validationRes.data && validationRes.data.status === false) {
          toast.error(validationRes.data.message || "Invalid account details");
          setIsLoading(false);

          return;
        }
      } catch (error) {
        toast.error(error?.response?.data?.message || "Invalid account details");
        setIsLoading(false);

        return;
      }
    }

    try {
      const bankAccount = bankAccounts[selectedBankIndex];
      const payload = {
        bank_details: [{
          id: bankAccount?.id || bankAccount?._id,
          account_name: bankDetails.account_name,
          account_number: bankDetails.account_number,
          bank_name: bankDetails.bank_name,
          bank_code: bankDetails.bank_code,
          currency: bankDetails.currency,
          action: "edit"
        }]
      };

      const response = await httprequest.patch(PROFILE_ENDPOINT, payload);
      toast.success(response?.data?.message || "Bank account updated successfully");

      closeEditModal();
      getBankDetails();
    } catch (error) {
      toast.error(error?.response?.data?.message || "Failed to update bank account");
    } finally {
      setIsLoading(false);
    }
  };

  const openDeleteModal = (index) => {
    setSelectedBankIndex(index);
    setIsDeleteBankModalOpen(true);
  };

  const closeDeleteModal = () => {
    setIsDeleteBankModalOpen(false);
    setSelectedBankIndex(null);
  };

  const handleDeleteConfirm = async () => {
    setIsLoading(true);
    try {
      const bankAccount = bankAccounts[selectedBankIndex];
      const payload = {
        bank_details: [{
          id: bankAccount?.id || bankAccount?._id,
          action: "delete"
        }]
      };

      const response = await httprequest.patch(PROFILE_ENDPOINT, payload);
      toast.success(response?.data?.message || "Bank account deleted successfully");

      closeDeleteModal();
      getBankDetails();
    } catch (error) {
      toast.error(error?.response?.data?.message || "Failed to delete bank account");
    } finally {
      setIsLoading(false);
    }
  };

  const handleAccordionChange = (index) => (event, isExpanded) => {
    setExpandedAccordion(isExpanded ? index : false);
  };

  return (
    <Layout
      children={
        <div>
          <div className="breadcrumb-container">
            <div>
              <BreadCrumb
                link_one="/wallet"
                link_one_name="Wallet"
                active="My Bank"
                description="Your teaching activity rewards and payouts"
              />
            </div>
            <Button
              buttonName="Add a Bank"
              variant="primary"
              buttonSize="medium"
              color="btndefault"
              onClick={toggleAddBankModal}
            />
          </div>

          <div className="page-content" style={{ marginTop: "20px" }}>
            {bankAccounts.length > 0 ? (
              <div>
                <div className={classes.bankingheader}>
                  <h4>Bank Accounts</h4>
                  <h6>Manage your added bank accounts</h6>
                </div>
                <Grid container spacing={3}>
                  {bankAccounts.map((bank, index) => (
                    <Grid item xs={12} md={6} key={index}>
                      <div style={{
                        borderRadius: "16px",
                        border: "1px solid #E4E7EC",
                        backgroundColor: "white",
                        overflow: "hidden"
                      }}
                      >
                        <Accordion
                          expanded={shouldCollapse ? expandedAccordion === index : true}
                          // eslint-disable-next-line no-undefined
                          onChange={shouldCollapse ? handleAccordionChange(index) : undefined}
                          sx={{
                            marginBottom: "0px",
                            boxShadow: "none",
                            padding: 0,
                            "&:before": { display: "none", padding: 0 },
                            height: "100%",
                            backgroundColor: "transparent"
                          }}
                        >
                          <AccordionSummary
                            expandIcon={<Icon icon="ep:arrow-down-bold" width="24" height="24" />}
                            aria-controls={`panel${index}-content`}
                            id={`panel${index}-header`}
                            style={{ backgroundColor: "#F0F2F5", borderRadius: "0px" }}
                          >
                            <strong>Account Info {index + 1}</strong>
                          </AccordionSummary>

                          <AccordionDetails sx={{ padding: "15px 20px" }}>
                            <Grid container spacing={2}>
                              <Grid item xs={12} md={6}>
                                <div className={classes.account_details}>
                                  <strong>Bank Name</strong>
                                  <span className={classes.span}>{bank.bank_name}</span>
                                </div>
                              </Grid>
                              <Grid item xs={12} md={6}>
                                <div className={classes.account_details}>
                                  <strong>Account Number</strong>
                                  <span className={classes.span}>{bank.account_number}</span>
                                </div>
                              </Grid>
                              <Grid item xs={12}>
                                <div className={classes.account_details}>
                                  <strong>Account Name</strong>
                                  <span className={classes.span}>{bank.account_name}</span>
                                </div>
                              </Grid>
                            </Grid>

                            <div style={{
                              display: "flex",
                              alignItems: "center",
                              borderTop: "1px solid #eee",
                              paddingTop: "10px",
                              marginTop: "10px",
                              gap: "20px"
                            }}
                            >
                              <span className={classes.Icon} style={{ color: "#0065FF" }} onClick={() => openEditModal(index)}>
                                <Icon icon="lucide:edit" width="18" />
                                    Edit Account
                              </span>
                              <span className={classes.Icon} style={{ color: "#E93831" }} onClick={() => openDeleteModal(index)}>
                                <Icon icon="material-symbols:delete-outline-rounded" width="18" />
                                    Delete Account
                              </span>
                            </div>
                          </AccordionDetails>
                        </Accordion>
                      </div>
                    </Grid>
                  ))}
                </Grid>
              </div>
            ) : (
              <div style={{
                backgroundColor: "white",
                minHeight: "60vh",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                borderRadius: "8px",
                marginTop: "20px"
              }}
              >
                <NoData
                  img={NoAccount}
                  title="No Bank Added"
                  subtitle="There has been no bank account added for payments yet."
                />
              </div>
            )}
          </div>

          <Modal
            title="Add a Bank Account"
            subtitle="Add your account for easy withdrawal"
            isopen={isAddBankModalOpen}
            closeModal={toggleAddBankModal}
            modalContent={
              <Grid container spacing={2} className="input-padding">
                <Grid item xs={12} md={6}>
                  <div>
                    <p className={classes.inputPickerTitle}>
                      Bank Name<span className={classes.inputRequired}>*</span>
                    </p>
                    <Select
                      options={bankOptions}
                      value={bankOptions.find(opt => opt.value === bankDetails.bank_name) || null}
                      onChange={(selectedOption) => setBankDetails(prev => ({
                        ...prev,
                        bank_name: selectedOption ? selectedOption.value : "",
                        bank_code: selectedOption ? selectedOption.code : ""
                      }))}
                      placeholder="Select Bank"
                      isSearchable
                      styles={{
                        menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                        control: (base) => ({
                          ...base,
                          height: "46px",
                          borderColor: "hsl(0, 0%, 80%)"
                        })
                      }}
                      menuPortalTarget={document.body}
                      menuPosition="absolute"
                    />
                  </div>
                </Grid>
                <Grid item xs={12} md={6}>
                  <InputGroup
                    label="Bank Code"
                    inputType="text"
                    inputName="bank_code"
                    value={bankDetails.bank_code || ""}
                    disabled
                    placeHolder="Auto-filled"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <InputGroup
                    label="Account Name"
                    required
                    inputType="text"
                    inputName="account_name"
                    value={bankDetails.account_name}
                    onChange={handleChange}
                    placeHolder="e.g. John Doe"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <InputGroup
                    label="Account Number"
                    required
                    inputType="text"
                    inputName="account_number"
                    value={bankDetails.account_number}
                    onChange={handleChange}
                    placeHolder="e.g. 0123456789"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <div>
                    <p className={classes.inputPickerTitle}>
                      Currency<span className={classes.inputRequired}>*</span>
                    </p>
                    <Select
                      options={currencyOptions}
                      value={getSelectedCurrency()}
                      onChange={handleCurrencyChange}
                      placeholder="Select currency"
                      isSearchable
                      styles={{
                        menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                        control: (base) => ({
                          ...base,
                          height: "46px",
                          borderColor: "hsl(0, 0%, 80%)"
                        })
                      }}
                      menuPortalTarget={document.body}
                      menuPosition="absolute"
                    />
                  </div>
                </Grid>
              </Grid>
            }
            modalFooter={
              <div className={classes.modalFooterBtn}>
                <Button
                  variant="primaryoutline"
                  buttonSize="medium"
                  color="btnfontprimary"
                  buttonName={isLoading ? "Adding..." : "Add an Account"}
                  disabled={isLoading}
                  onClick={handleSubmit}
                />
              </div>
            }
          />

          {/* Edit Bank Account Modal */}
          <Modal
            title="Edit Bank Account"
            subtitle="Update your bank account details"
            isopen={isEditBankModalOpen}
            closeModal={closeEditModal}
            modalContent={
              <Grid container spacing={2} className="input-padding">
                <Grid item xs={12} md={6}>
                  <div>
                    <p className={classes.inputPickerTitle}>
                      Bank Name<span className={classes.inputRequired}>*</span>
                    </p>
                    <Select
                      options={bankOptions}
                      value={bankOptions.find(opt => opt.value === bankDetails.bank_name) || null}
                      onChange={(selectedOption) => setBankDetails(prev => ({
                        ...prev,
                        bank_name: selectedOption ? selectedOption.value : "",
                        bank_code: selectedOption ? selectedOption.code : ""
                      }))}
                      placeholder="Select Bank"
                      isSearchable
                      styles={{
                        menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                        control: (base) => ({
                          ...base,
                          height: "46px",
                          borderColor: "hsl(0, 0%, 80%)"
                        })
                      }}
                      menuPortalTarget={document.body}
                      menuPosition="absolute"
                    />
                  </div>
                </Grid>
                <Grid item xs={12} md={6}>
                  <InputGroup
                    label="Bank Code"
                    inputType="text"
                    inputName="bank_code"
                    value={bankDetails.bank_code || ""}
                    disabled
                    placeHolder="Auto-filled"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <InputGroup
                    label="Account Name"
                    required
                    inputType="text"
                    inputName="account_name"
                    value={bankDetails.account_name}
                    onChange={handleChange}
                    placeHolder="e.g. John Doe"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <InputGroup
                    label="Account Number"
                    required
                    inputType="text"
                    inputName="account_number"
                    value={bankDetails.account_number}
                    onChange={handleChange}
                    placeHolder="e.g. 0123456789"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <div>
                    <p className={classes.inputPickerTitle}>
                      Currency<span className={classes.inputRequired}>*</span>
                    </p>
                    <Select
                      options={currencyOptions}
                      value={getSelectedCurrency()}
                      onChange={handleCurrencyChange}
                      placeholder="Select currency"
                      isSearchable
                      styles={{
                        menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                        control: (base) => ({
                          ...base,
                          height: "46px",
                          borderColor: "hsl(0, 0%, 80%)"
                        })
                      }}
                      menuPortalTarget={document.body}
                      menuPosition="absolute"
                    />
                  </div>
                </Grid>
              </Grid>
            }
            modalFooter={
              <>
                <div className="flex gap-10">
                  <Button
                    variant="primaryoutline"
                    buttonName="Cancel"
                    buttonSize="full"
                    color="btnfontprimary"
                    onClick={closeEditModal}
                  />
                  <Button
                    variant="primary"
                    buttonName={isLoading ? "Updating..." : "Update Account"}
                    buttonSize="full"
                    color="btndefault"
                    disabled={isLoading}
                    onClick={handleEditSubmit}
                  />
                </div>
              </>
            }
          />

          {/* Delete Bank Account Modal */}
          <Modal
            title="Delete Bank Account"
            subtitle={
              <div>
                Are you sure you want to remove{" "}
                <span className="modal-name-highlight">
                  {selectedBankIndex !== null && bankAccounts[selectedBankIndex]
                    ? bankAccounts[selectedBankIndex].bank_name
                    : ""}
                </span> {" "}
                {" "}from your bank accounts? Removing it will permanently delete
                this account.
              </div>
            }
            modalContent={<></>}
            modalFooter={
              <>
                <div className="flex gap-10">
                  <Button
                    variant="dangeroutline"
                    buttonSize="full"
                    color="btndanger"
                    buttonName="Cancel"
                    onClick={closeDeleteModal}
                  />
                  <Button
                    variant="danger"
                    buttonName={isLoading ? "Deleting..." : "Delete Account"}
                    buttonSize="full"
                    color="btndefault"
                    disabled={isLoading}
                    onClick={handleDeleteConfirm}
                  />
                </div>
              </>
            }
            isopen={isDeleteBankModalOpen}
            closeModal={closeDeleteModal}
          />
        </div>
      }
    />
  );
};

export default MyBank;
